import React, { useState } from 'react';
import ResourceCard from '../../../shared/ResourceSidebar/components/ResourceCard';

/**
 * @component ProjectTeamList
 * @description Renders the assigned team members and vehicles list using ResourceCard tiles.
 * Supports drill-down for Kolonnen.
 */
const ProjectTeamList = ({ 
    displayList, 
    teamView,
    setTeamView,
    activeTab, 
    setActiveTab, 
    editingUser, 
    setEditingUser, 
    currentUser, 
    project, 
    handleUserSelect, 
    handleToggleResource,
    handleRemoveAllResources,
    readOnly,
    availableWorkers,
    availableVehicles,
    onDoubleClick,
    getWorkerStatus,
    getVehicleStatus
}) => {
    const [expandedKolonneId, setExpandedKolonneId] = useState(null);

    const toggleKolonneExpand = (id) => {
        setExpandedKolonneId(prev => prev === id ? null : id);
    };

    // Helper to adapt assigned user data for ResourceCard
    const adaptUserForCard = (u) => ({
        id: u.worker_id,
        name: u.name,
        plate: u.plate,
        license_plate: u.plate,
        internal_name: u.internal_name, // Added for vehicles
        role: u.role || u.employee_role || 'Mitglied',
        global_role: u.global_role,
        color: u.color
    });

    return (
        <div className="team-assigned-list">
            <div className="team-assigned-list-header">
                <div className="co-segmented-control main-view-switcher">
                    <button
                        type="button"
                        onClick={() => { setTeamView('team'); setEditingUser(null); }}
                        className={teamView === 'team' ? 'active' : ''}
                    >
                        👥 Projekt-Team
                    </button>
                    <button
                        type="button"
                        onClick={() => { setTeamView('blacklist'); setEditingUser(null); }}
                        className={teamView === 'blacklist' ? 'active' : ''}
                    >
                        🚫 Blacklist
                    </button>
                </div>
            </div>

            {teamView === 'team' && (
                <div className="team-assigned-list-filters">
                    <div className="filter-pills">
                        <button
                            type="button"
                            onClick={() => { setActiveTab('personal'); setEditingUser(null); }}
                            className={activeTab === 'personal' ? 'active' : ''}
                        >
                            👤 Personal ({displayList.workers.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => { setActiveTab('fahrzeuge'); setEditingUser(null); }}
                            className={activeTab === 'fahrzeuge' ? 'active' : ''}
                        >
                            🚛 Fahrzeuge ({displayList.vehicles.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => { setActiveTab('kolonnen'); setEditingUser(null); }}
                            className={activeTab === 'kolonnen' ? 'active' : ''}
                        >
                            👥 Kolonnen ({displayList.kolonnen.length})
                        </button>
                    </div>
                </div>
            )}

            {teamView === 'team' && !readOnly && (displayList.workers.length > 0 || displayList.vehicles.length > 0 || displayList.kolonnen.length > 0) && (
                <div className="team-list-actions">
                    <button 
                        type="button" 
                        className="btn-remove-all-resources" 
                        onClick={handleRemoveAllResources}
                    >
                        🗑️ Alle Ressourcen entfernen
                    </button>
                </div>
            )}

            <div className="team-list-content px-2 py-2">
                {displayList.viewItems.length === 0 && displayList.kolonnen.length === 0 && (
                    <div className="empty-team-state">
                        <div className="empty-icon" style={{ fontSize: '3rem', opacity: 0.2, marginBottom: '16px' }}>
                            {teamView === 'blacklist' ? '🚫' : (activeTab === 'personal' ? '👤' : activeTab === 'fahrzeuge' ? '🚛' : '👥')}
                        </div>
                        <div style={{ fontWeight: 600, color: '#64748b' }}>
                            {teamView === 'blacklist' ? 'Keine geblockten Nutzer' : 'Liste leer'}
                        </div>
                    </div>
                )}

                {teamView === 'team' && activeTab === 'personal' && (
                    <div className="resource-list">
                        {displayList.viewItems.map(u => (
                            <div key={`worker-${u.worker_id}`} onClick={() => handleUserSelect(u)} className={editingUser?.worker_id === u.worker_id ? 'is-editing' : ''} style={{ cursor: 'pointer' }}>
                                <ResourceCard 
                                    resource={adaptUserForCard(u)}
                                    type="worker"
                                    isAssigned={true}
                                    isEditMode={!readOnly}
                                    isDraggable={false}
                                    onToggle={(res, t) => handleToggleResource(res, t)}
                                    onDoubleClick={onDoubleClick}
                                    status={getWorkerStatus ? getWorkerStatus(u.worker_id) : null}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {teamView === 'team' && activeTab === 'fahrzeuge' && (
                    <div className="resource-list">
                        {displayList.viewItems.map(u => (
                            <div 
                                key={`vehicle-${u.worker_id}`} 
                                onClick={() => handleUserSelect(u)} 
                                className={editingUser?.worker_id === u.worker_id ? 'is-editing' : ''}
                                style={{ cursor: 'pointer' }}
                            >
                                <ResourceCard 
                                    resource={adaptUserForCard(u)}
                                    type="vehicle"
                                    isAssigned={true}
                                    isEditMode={!readOnly}
                                    isDraggable={false}
                                    onToggle={(res, t) => handleToggleResource(res, t)}
                                    onDoubleClick={onDoubleClick}
                                    status={getVehicleStatus ? getVehicleStatus(u.worker_id) : null}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {teamView === 'team' && activeTab === 'kolonnen' && (
                    <div className="resource-list">
                        {displayList.kolonnen.map(k => {
                            const isExpanded = expandedKolonneId === k.id;
                            const sortedMemberIds = k.memberIds || [];

                            return (
                                <div key={`kolonne-${k.id}`} className="kolonne-drilldown-item">
                                    <div onClick={() => toggleKolonneExpand(k.id)} style={{ cursor: 'pointer' }}>
                                        <ResourceCard 
                                            resource={k}
                                            type="kolonne"
                                            isAssigned={true}
                                            isEditMode={!readOnly}
                                            isDraggable={false}
                                            onToggle={(res, t) => handleToggleResource(res, t)}
                                            onDoubleClick={onDoubleClick}
                                        />
                                    </div>
                                    {isExpanded && (
                                        <div className="kolonne-members-expansion pl-8 pt-1 flex flex-column gap-1">
                                            {/* WORKERS SECTION */}
                                            {(k.members || []).filter(m => m.type === 'worker').map(m => {
                                                const mid = String(m.resource_id);
                                                const worker = availableWorkers.find(w => String(w.id) === mid);
                                                const assignedMember = displayList.workers.find(u => String(u.worker_id) === mid);
                                                if (!worker && !assignedMember) return null;

                                                return (
                                                    <div 
                                                        key={`member-w-${k.id}-${mid}`} 
                                                        onClick={() => assignedMember && handleUserSelect(assignedMember)}
                                                        className={editingUser?.worker_id === mid ? 'is-editing' : ''}
                                                        style={{ cursor: assignedMember ? 'pointer' : 'default' }}
                                                    >
                                                        <ResourceCard 
                                                            resource={assignedMember ? adaptUserForCard(assignedMember) : worker}
                                                            type="worker"
                                                            isAssigned={true}
                                                            isEditMode={false}
                                                            isDraggable={false}
                                                            onDoubleClick={onDoubleClick}
                                                            status={getWorkerStatus ? getWorkerStatus(mid) : null}
                                                        />
                                                    </div>
                                                );
                                            })}

                                            {/* VISUAL DIVIDER (only if both types exist) */}
                                            {(k.members || []).some(m => m.type === 'worker') && 
                                             (k.members || []).some(m => m.type === 'vehicle') && (
                                                <div className="kolonne-drilldown-divider"></div>
                                            )}

                                            {/* VEHICLES SECTION */}
                                            {(k.members || []).filter(m => m.type === 'vehicle').map(m => {
                                                const mid = String(m.resource_id);
                                                const vehicle = availableVehicles.find(v => String(v.id) === mid);
                                                const assignedMember = displayList.vehicles.find(u => String(u.worker_id) === mid);
                                                if (!vehicle && !assignedMember) return null;

                                                return (
                                                    <div 
                                                        key={`member-v-${k.id}-${mid}`} 
                                                        onClick={() => assignedMember && handleUserSelect(assignedMember)}
                                                        className={editingUser?.worker_id === mid ? 'is-editing' : ''}
                                                        style={{ cursor: assignedMember ? 'pointer' : 'default' }}
                                                    >
                                                        <ResourceCard 
                                                            resource={assignedMember ? adaptUserForCard(assignedMember) : vehicle}
                                                            type="vehicle"
                                                            isAssigned={true}
                                                            isEditMode={false}
                                                            isDraggable={false}
                                                            onDoubleClick={onDoubleClick}
                                                            status={getVehicleStatus ? getVehicleStatus(mid) : null}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {teamView === 'blacklist' && (
                    <div className="resource-list">
                        {displayList.viewItems.map(u => (
                            <div key={`blacklist-${u.worker_id}`} onClick={() => handleUserSelect(u)} className={editingUser?.worker_id === u.worker_id ? 'is-editing' : ''} style={{ cursor: 'pointer' }}>
                                <ResourceCard 
                                    resource={adaptUserForCard(u)}
                                    type={(u.project_role || u.role) === 'Fahrzeug' ? 'vehicle' : 'worker'}
                                    isAssigned={true}
                                    isEditMode={!readOnly}
                                    isDraggable={false}
                                    onToggle={(res, t) => handleToggleResource(res, t)}
                                    onDoubleClick={onDoubleClick}
                                    status={(u.project_role || u.role) === 'Fahrzeug' ? (getVehicleStatus ? getVehicleStatus(u.worker_id) : null) : (getWorkerStatus ? getWorkerStatus(u.worker_id) : null)}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectTeamList;
